import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button, TextButton } from '@xing-com/button';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import type { SearchAlertFilters } from '@xing-com/crate-jobs-helpers';
import { isValidEmail } from '@xing-com/crate-jobs-helpers';
import {
  useCreateSearchAlertByQuery,
  useCreateSearchAlertLO,
} from '@xing-com/crate-jobs-hooks';
import { ROUTES } from '@xing-com/crate-jobs-paths';
import { unscrambleStringId } from '@xing-com/crate-utils-x1-data';
import { Link } from '@xing-com/link';
import { FormField } from '@xing-com/text-field';
import { spaceS } from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { FiltersSummary } from './filters-summary';
import { Title } from './styles';

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${spaceS};
`;

type Props = {
  query?: SearchAlertFilters;
  isThirdParty: boolean;
  onCompleted: (alertId: string) => void;
  onCancel: () => void;
};

export const CreateAlert: React.FC<Props> = ({
  onCompleted,
  onCancel,
  query,
  isThirdParty,
}) => {
  const intl = useIntl();
  const { isLoggedOut } = useLoginState();
  const [email, setEmail] = useState('');
  const [showEmailError, setShowEmailError] = useState(false);
  const nwtPage = isThirdParty ? 'jobs/detail_external' : 'jobs/detail';
  const createSearchAlertLI = useCreateSearchAlertByQuery({
    nwtPage,
  });
  const createSearchAlertLO = useCreateSearchAlertLO({
    nwtPage,
  });

  const handleOnCreate = (): void => {
    if (isLoggedOut) {
      const hasError = !isValidEmail(email);
      setShowEmailError(hasError);

      if (hasError) return;
    }

    if (isLoggedOut) {
      const disciplineIds =
        query?.filter?.discipline?.id
          .filter((id): id is string => id !== null)
          .map(unscrambleStringId) ?? [];
      const location = query?.location?.text;
      const radius = String(query?.location?.radius);
      createSearchAlertLO({
        variables: {
          email,
          query: {
            keywords: query?.keywords,
            location,
            radius,
            filters: { discipline: disciplineIds },
          },
        },
        onCompleted: () => onCompleted('logged_out'),
      });
    } else {
      const outputQuery = {
        ...query,
        filter: {
          ...query?.filter,
          discipline: { id: query?.filter?.discipline?.id ?? [] },
        },
      }; // we need to remove the localizationValue from the discipline filter, as it's not supported by the backend
      createSearchAlertLI({
        variables: {
          ...outputQuery,
        },
        onCompleted: (response) => {
          if (
            response.createSearchAlertByQuery?.__typename ===
            'SearchAlertCreationSuccess'
          ) {
            onCompleted(response.createSearchAlertByQuery.alert.globalId);
          }
        },
      });
    }
  };

  const handleOnCancel = (): void => {
    onCancel();
  };

  return (
    <>
      <div>
        <Title size="xxlarge">
          <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT_HEADER" />
        </Title>
        <BodyCopy noMargin>
          <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT_PARAGRAPH" />
        </BodyCopy>
      </div>
      {query ? <FiltersSummary query={query} /> : null}
      {isLoggedOut ? (
        <div>
          <FormField
            size="medium"
            value={email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
            label={intl.formatMessage({
              id: 'JOBS_SEARCH_ALERT_LO_MODAL_EMAIL_PLACEHOLDER',
            })}
            error={showEmailError}
            helperText={
              showEmailError
                ? intl.formatMessage({
                    id: 'JOBS_SEARCH_ALERT_LO_MODAL_EMAIL_ERROR',
                  })
                : ''
            }
          />

          <FormattedMessage
            id="JOBS_SEARCH_ALERT_LO_MODAL_LEGAL_TEXT"
            values={{
              a1: (chunks) => (
                <Link to={ROUTES.terms} target="_blank" rel="noopener">
                  <strong>{chunks}</strong>
                </Link>
              ),
              a2: (chunks) => (
                <Link to={ROUTES.privacy} target="_blank" rel="noopener">
                  <strong>{chunks}</strong>
                </Link>
              ),
            }}
          />
        </div>
      ) : null}
      <Buttons>
        <Button variant="primary" size="medium" onClick={handleOnCreate}>
          <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT_CONFIRM" />
        </Button>
        <TextButton size="medium" onClick={handleOnCancel}>
          <FormattedMessage id="JOBS_CREATE_SEARCH_ALERT_CANCEL" />
        </TextButton>
      </Buttons>
    </>
  );
};
